import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---
export interface WorkspaceGlossaryEntry {
  glossaryId: string;
  workspaceId: string;
  term: string;
  definition: string;
  aliases: string[];
  category: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
    // CHG-133 Step 7
    displayColor?: string | null;
    nickname?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  };
}

export interface GlossaryListResponse {
  data: WorkspaceGlossaryEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  categories: string[];
}

export interface CreateWorkspaceGlossaryData {
  term: string;
  definition: string;
  aliases?: string[];
  category?: string;
}

export interface UpdateWorkspaceGlossaryData {
  term?: string;
  definition?: string;
  aliases?: string[];
  category?: string | null;
}

// --- API calls ---
export async function fetchGlossaryEntries(
  workspaceId: string,
  params?: {
    q?: string;
    category?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  },
) {
  const query = new URLSearchParams();
  if (params?.q) query.set('q', params.q);
  if (params?.category) query.set('category', params.category);
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.sortBy) query.set('sortBy', params.sortBy);
  if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
  const qs = query.toString();
  const res = await api.get<ApiResponse<GlossaryListResponse>>(
    `/workspaces/${workspaceId}/glossary${qs ? `?${qs}` : ''}`,
  );
  return res.data.data!;
}

export async function fetchGlossaryEntry(workspaceId: string, glossaryId: string) {
  const res = await api.get<ApiResponse<WorkspaceGlossaryEntry>>(
    `/workspaces/${workspaceId}/glossary/${glossaryId}`,
  );
  return res.data.data!;
}

export async function createGlossaryEntry(
  workspaceId: string,
  data: CreateWorkspaceGlossaryData,
) {
  const res = await api.post<ApiResponse<WorkspaceGlossaryEntry>>(
    `/workspaces/${workspaceId}/glossary`,
    data,
  );
  return res.data.data!;
}

export async function updateGlossaryEntry(
  workspaceId: string,
  glossaryId: string,
  data: UpdateWorkspaceGlossaryData,
) {
  const res = await api.put<ApiResponse<WorkspaceGlossaryEntry>>(
    `/workspaces/${workspaceId}/glossary/${glossaryId}`,
    data,
  );
  return res.data.data!;
}

export async function deleteGlossaryEntry(workspaceId: string, glossaryId: string) {
  await api.delete(`/workspaces/${workspaceId}/glossary/${glossaryId}`);
}

export async function fetchGlossaryCategories(workspaceId: string) {
  const res = await api.get<ApiResponse<{ categories: string[] }>>(
    `/workspaces/${workspaceId}/glossary/categories`,
  );
  return res.data.data!.categories;
}
